import { BookOpen, Calendar, ChevronRight, UserCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import type { TaughtSection } from './profileSummary';

export interface TeacherSubjectsTabProps {
  sections: TaughtSection[];
}

/**
 * Every section the teacher is allotted this year, with the subjects they take
 * in it and the children on its roll — read from their own allotments.
 */
export function TeacherSubjectsTab({ sections }: TeacherSubjectsTabProps) {
  const navigate = useNavigate();

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="card px-6 py-4 space-y-4">
        <div className="flex items-center gap-3 border-b border-line pb-4">
          <BookOpen className="w-6 h-6 text-indigo-600 dark:text-indigo-400" aria-hidden />
          <h3 className="text-lg font-black text-ink">Assigned Classes & Subjects</h3>
        </div>

        {sections.length === 0 ? (
          <EmptyState
            icon={<BookOpen className="h-5 w-5" />}
            title="No classes allotted yet"
            description="Once the school office allots you a class or a subject this year, it appears here."
          />
        ) : (
          <div className="space-y-3.5">
            {sections.map((section) => (
              <div
                key={section.uuid}
                className="p-5 rounded-2xl bg-surface-2 border border-line flex items-center justify-between gap-3"
              >
                <div className="min-w-0">
                  <h4 className="text-base font-bold text-ink flex flex-wrap items-center gap-2">
                    {section.displayName}
                    {section.isClassTeacher && (
                      <span className="inline-flex items-center gap-1 rounded-pill bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-500/30 px-2 py-0.5 text-[11px] font-black">
                        <UserCheck className="h-3 w-3" aria-hidden />
                        Class teacher
                      </span>
                    )}
                  </h4>
                  <p className="text-sm font-medium text-ink-2 mt-0.5">
                    {section.subjects.length > 0
                      ? `${section.subjects.length === 1 ? 'Subject' : 'Subjects'}: ${section.subjects.join(', ')}`
                      : 'Class teacher only — no subject'}
                  </p>
                </div>
                <span className="shrink-0 px-3.5 py-1 rounded-pill text-xs font-black bg-indigo-50 text-indigo-700 border border-indigo-200 dark:bg-indigo-500/15 dark:text-indigo-300 dark:border-indigo-500/30">
                  {section.totalStudents} {section.totalStudents === 1 ? 'Student' : 'Students'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card px-6 py-4 space-y-4">
        <div className="flex items-center gap-3 border-b border-line pb-4">
          <Calendar className="w-6 h-6 text-sky-600 dark:text-sky-400" aria-hidden />
          <h3 className="text-lg font-black text-ink">Quick Schedule Access</h3>
        </div>
        <p className="text-sm font-medium text-ink-2 leading-relaxed">
          View your timetable periods, weekly routine, and active classes.
        </p>
        <Button
          size="lg"
          variant="secondary"
          rightIcon={<ChevronRight className="w-5 h-5" />}
          onClick={() => navigate('/teacher/timetable')}
        >
          Go to Full Timetable
        </Button>
      </div>
    </div>
  );
}
